import { TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const data = [
  { age: 7, weight: 185, industry: 150, feed: 165, fcr: 0.89, livability: 99.5 },
  { age: 14, weight: 522, industry: 415, feed: 530, fcr: 1.016, livability: 99.0 },
  { age: 21, weight: 1021, industry: 820, feed: 1185, fcr: 1.16, livability: 98.5 },
  { age: 28, weight: 1632, industry: 1290, feed: 2123, fcr: 1.301, livability: 98.0 },
  { age: 35, weight: 2307, industry: 1830, feed: 3251, fcr: 1.409, livability: 97.5 },
  { age: 42, weight: 3000, industry: 2380, feed: 4500, fcr: 1.5, livability: 97.0 },
];

const PerformanceSection = () => {
  return (
    <section id="performance" className="py-32 bg-primary text-primary-foreground relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute left-0 top-0 w-1/4 h-full bg-gradient-to-r from-primary-glow/20 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          <div className="lg:col-span-5">
            <span className="text-accent font-body text-xs uppercase tracking-[0.25em] font-semibold">Results</span>
            <h2 className="text-5xl md:text-6xl font-bold font-display mt-3 leading-[1.05]">
              Proven
              <br />
              <span className="italic font-normal text-accent">performance</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-primary-foreground/60 font-body text-lg leading-relaxed">
              Field trial data from 120+ farms across Haryana, Punjab, and UP consistently outperforms industry averages.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Chart */}
          <div className="lg:col-span-2 bg-primary-foreground/5 rounded-2xl border border-primary-foreground/10 p-8">
            <h3 className="text-lg font-bold font-display mb-6 flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-accent" />
              Growth Curve — Sandhu vs Industry Average (42 days)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="age" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 12, fill: "rgba(255,255,255,0.5)" }} label={{ value: "Day", position: "insideBottomRight", offset: -5, fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
                <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 12, fill: "rgba(255,255,255,0.5)" }} label={{ value: "Weight (g)", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "hsl(152 56% 12%)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", color: "#fff", fontSize: "13px" }}
                />
                <Legend wrapperStyle={{ fontSize: "12px", color: "rgba(255,255,255,0.6)" }} />
                <Line type="monotone" dataKey="weight" name="Sandhu Feeds" stroke="hsl(25 60% 45%)" strokeWidth={3} dot={{ fill: "hsl(25 60% 45%)", r: 5 }} />
                <Line type="monotone" dataKey="industry" name="Industry Avg" stroke="rgba(255,255,255,0.3)" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "rgba(255,255,255,0.3)", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>

            {/* Table below chart */}
            <div className="mt-8 -mx-2 sm:mx-0 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm font-body">
                <thead>
                  <tr className="text-primary-foreground/40 text-xs uppercase tracking-wider">
                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Day</th>
                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Sandhu (g)</th>
                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Industry (g)</th>
                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Feed (g)</th>
                    <th className="text-left pb-3 pr-4 whitespace-nowrap">FCR</th>
                    <th className="text-left pb-3 pr-4 whitespace-nowrap">Livability %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="border-t border-primary-foreground/5">
                      <td className="py-2.5 pr-4 font-semibold whitespace-nowrap">{row.age}</td>
                      <td className="py-2.5 pr-4 text-accent font-semibold whitespace-nowrap">{row.weight}</td>
                      <td className="py-2.5 pr-4 text-primary-foreground/40 whitespace-nowrap">{row.industry}</td>
                      <td className="py-2.5 pr-4 text-primary-foreground/70 whitespace-nowrap">{row.feed}</td>
                      <td className="py-2.5 pr-4 text-primary-foreground/70 whitespace-nowrap">{row.fcr.toFixed(2)}</td>
                      <td className="py-2.5 pr-4 text-primary-foreground/70 whitespace-nowrap">{row.livability}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-primary-foreground/30 font-body mt-4 italic">
              * Trial data: Cobb 430 breed, 120 farms, Haryana-Punjab, 2023–2025 production cycles. Industry avg sourced from ICAR poultry reports.
            </p>
          </div>

          {/* Highlights */}
          <div className="space-y-6">
            {[
              { value: "1.50", label: "Final FCR at 42 days", sub: "Significantly better than industry average of 1.85" },
              { value: "3.0kg", label: "Live weight at 6 weeks", sub: "620g above industry average of 2.38 kg" },
              { value: "97.0%", label: "Livability rate", sub: "vs industry average of 93–94%" },
              { value: "72%+", label: "Dressing percentage", sub: "Superior carcass yield and meat quality" },
            ].map((h, i) => (
              <div key={i} className="bg-primary-foreground/5 rounded-2xl border border-primary-foreground/10 p-8">
                <div className="text-4xl font-bold text-accent font-display mb-1">{h.value}</div>
                <p className="text-sm font-body font-semibold">{h.label}</p>
                <p className="text-xs text-primary-foreground/40 font-body mt-1">{h.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PerformanceSection;
